import { z } from 'zod';

import { UuidSchema } from './common.js';

/**
 * Global search across the admin app's four "anchor" entities: Leads,
 * Customers, Packages, Blogs. Powers the ⌘K palette's content search.
 *
 * Results are intentionally lightweight; the user navigates to the entity's
 * detail page for full context.
 */

export const GLOBAL_SEARCH_KINDS = ['leads', 'customers', 'packages', 'blogs'] as const;
export const GlobalSearchKindSchema = z.enum(GLOBAL_SEARCH_KINDS);
export type GlobalSearchKind = z.infer<typeof GlobalSearchKindSchema>;

export const GlobalSearchQuerySchema = z.object({
  q: z.string().trim().min(1).max(120),
  /**
   * Optional comma-separated list of kinds to query. Omit to search all
   * four. Useful when the caller (e.g. a typed-ahead picker) only cares
   * about one bucket.
   */
  kinds: z
    .string()
    .trim()
    .optional()
    .transform((raw) => {
      if (!raw) return undefined;
      const parts = raw
        .split(',')
        .map((p) => p.trim())
        .filter((p): p is GlobalSearchKind =>
          (GLOBAL_SEARCH_KINDS as readonly string[]).includes(p),
        );
      return parts.length > 0 ? parts : undefined;
    }),
  /** Per-bucket cap. Server clamps to [1, 10]; default 5. */
  limit: z.coerce.number().int().min(1).max(10).optional(),
});
export type GlobalSearchQuery = z.infer<typeof GlobalSearchQuerySchema>;

/** Common surface across all result kinds; client renders these uniformly. */
const GlobalSearchHitBaseSchema = z.object({
  id: UuidSchema,
  /** Primary line - e.g. customer name, lead reference, package title, blog title. */
  title: z.string(),
  /** Secondary line - e.g. customer email, lead title, package category, blog excerpt. */
  subtitle: z.string().nullable(),
  /** Pre-built admin route, e.g. `/leads/<id>`. The client just navigates. */
  to: z.string(),
});

export const GlobalSearchLeadHitSchema = GlobalSearchHitBaseSchema.extend({
  kind: z.literal('leads'),
});
export type GlobalSearchLeadHit = z.infer<typeof GlobalSearchLeadHitSchema>;

export const GlobalSearchCustomerHitSchema = GlobalSearchHitBaseSchema.extend({
  kind: z.literal('customers'),
});
export type GlobalSearchCustomerHit = z.infer<typeof GlobalSearchCustomerHitSchema>;

export const GlobalSearchPackageHitSchema = GlobalSearchHitBaseSchema.extend({
  kind: z.literal('packages'),
});
export type GlobalSearchPackageHit = z.infer<typeof GlobalSearchPackageHitSchema>;

export const GlobalSearchBlogHitSchema = GlobalSearchHitBaseSchema.extend({
  kind: z.literal('blogs'),
});
export type GlobalSearchBlogHit = z.infer<typeof GlobalSearchBlogHitSchema>;

export const GlobalSearchResponseSchema = z.object({
  q: z.string(),
  leads: z.array(GlobalSearchLeadHitSchema),
  customers: z.array(GlobalSearchCustomerHitSchema),
  packages: z.array(GlobalSearchPackageHitSchema),
  blogs: z.array(GlobalSearchBlogHitSchema),
});
export type GlobalSearchResponse = z.infer<typeof GlobalSearchResponseSchema>;
